import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { Stack, Text } from '@/design-system'
import { PassMark } from '@/app/Logo'
import { STEP_PATH, clearReturn, peekReturn, readDraft, rememberReturn, resumePath } from '@/features/onboarding/progress'
import { useAuth } from './auth-context'

function Splash() {
  return (
    <Stack align="center" justify="center" gap={4} style={{ minHeight: '100dvh' }}>
      <PassMark size={40} />
      <Text variant="caption" tone="secondary">
        Securing your session…
      </Text>
    </Stack>
  )
}

/**
 * Everything in the app is behind login. The place a signed-out visitor was
 * heading is kept, so after logging in they land where the link pointed.
 */
export function RequireAuth() {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'checking') return <Splash />
  if (status === 'anonymous') {
    const target = location.pathname + location.search
    if (target !== '/') rememberReturn(target)
    return <Navigate to="/login" replace />
  }
  return <Outlet />
}

/** The app itself is only for people who have finished onboarding. */
export function RequireOnboarded() {
  const { onboarded } = useAuth()
  if (!onboarded) return <Navigate to={resumePath()} replace />
  return <Outlet />
}

/**
 * Keeps a signed-in person out of the welcome, sign-up and login screens, and
 * decides where they go instead. This is the only place that happens, so a
 * screen never has to navigate after signing someone in.
 */
export function RedirectIfAuthenticated() {
  const { status, onboarded } = useAuth()

  if (status !== 'authenticated') return <Outlet />

  if (!onboarded) {
    // Only a registration in progress gets here. Account creation is already
    // behind them, so never send them back to it — that would loop.
    const step = readDraft().step
    return <Navigate to={step === 'create' ? STEP_PATH.verify : resumePath()} replace />
  }

  return <ReturnTo />
}

/**
 * Sends a signed-in person back to the link they started from. The target is
 * read once into state and cleared after the fact: reading and deleting it in
 * the same render lost it whenever React rendered twice.
 */
function ReturnTo() {
  const [target] = useState(() => peekReturn() ?? '/')
  useEffect(() => clearReturn(), [])
  return <Navigate to={target} replace />
}
